import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject, takeUntil} from "rxjs";
import {Project} from "../../shared/models/project.model";
import {CoreService} from "../../shared/services/core.service";
import {ProjectsService} from "../../shared/services/projects.service";
import {ToastrService} from "ngx-toastr";
import {ActivatedRoute, Router} from "@angular/router";
import {ThemesService} from "../../shared/services/themes.service";
import {DomSanitizer} from "@angular/platform-browser";
import {FormControl, FormGroup, Validators, AbstractControl} from "@angular/forms";
import {ProjectSettings} from "../../shared/models/project-settings/project-settings.model";
import {ActFields} from "../../shared/models/project-settings/selected-fields/act-fields.model";
import {
  ActRequiredFileCategories
} from "../../shared/models/project-settings/required-file-categories/act-required-file-categories.model";
import {ActConfiguration} from "../../shared/models/act-configuration.model";
import {
  FieldSelectedConfiguration
} from "../../shared/models/field-configuration-selected-field/field-configuration-selected-field.model";
import {DownloadsService} from "../../shared/services/downloads.service";
import {SessionsService} from "../../shared/services/sessions.service";
import {FilesService} from "../../shared/services/files.service";
import { FormDataService, ActFormData } from '../../shared/services/form-data.service';

@Component({
  selector: 'app-act',
  templateUrl: './act.component.html',
  styleUrls: ['./act.component.sass']
})
export class ActComponent implements OnInit, OnDestroy {
  public projectShortName: string | null = '';

  private ngUnsubscribe = new Subject();

  public project: Project = new Project();

  public projectSettings: ProjectSettings | null | undefined = new ProjectSettings();

  public actConfiguration: ActConfiguration | null | undefined = new ActConfiguration();

  public actFields: FieldSelectedConfiguration[] | null | undefined = [];

  public actRequiredFileCategories: ActRequiredFileCategories[] | null | undefined = [];

  public loadingRedirect = false;

  public showErrorMessageProjectNotActive = false;

  public showErrorMessageStepCompleted = false;

  public errorCode: string = '';

  public stepCompletedErrorMessage: string = '';

  public loading = true;

  public dynamicForm: FormGroup = new FormGroup({});

  public formModel: any[] = [];

  public downloadFiles: any[] = [];

  public showDownloadbleFiles = false;

  public uploadOfFilesRequired = false;

  public tokenIdentifier: string = '';

  public sessionToken: string = '';

  public availableFiles: any;

  public session: any;

  public filesByCategory: { [key: string]: any[] } = {};

  constructor(
    private coreService: CoreService,
    private projectService: ProjectsService,
    private toastrService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private themeService: ThemesService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private downloadService: DownloadsService,
    private sessionService: SessionsService,
    private fileService: FilesService,
    private formDataService: FormDataService
  ) {
    this.coreService.enableSidebar();
    if (this.activatedRoute.snapshot.params['projectShortname']) {

      this.projectShortName = this.activatedRoute.snapshot.params['projectShortname'];

      this.themeService.setProjectShortName(this.projectShortName);
    } else {
      this.router.navigate(['/404']);
    }
  }

  ngOnInit() {
    this.projectShortName = this.activatedRoute.snapshot.paramMap.get('projectShortname');
    if (this.projectShortName) {
      this.projectShortName = this.projectShortName.toUpperCase();

      this.projectService.getProject(this.projectShortName).pipe(
        takeUntil(this.ngUnsubscribe)
      ).subscribe({
        next: (project: Project) => {
          this.themeService.checkThemeSettings(this.projectShortName);
          this.projectService.setLoading(true);
          this.project = project;
          this.projectService.setProject(project);
          const isProjectActive = this.projectService.isProjectActive();

          if (!isProjectActive) {
            this.showErrorMessageProjectNotActive = true;
          } else {
            // load the rest
            // load downloads
            if (this.activatedRoute.snapshot.params['tokenIdentifier']) {
              this.tokenIdentifier = this.activatedRoute.snapshot.params['tokenIdentifier'];
              if (this.projectShortName) {
                this.sessionService.getSessionToken(this.projectShortName, this.tokenIdentifier).subscribe((x: any) => {

                  // Check if the step has already been completed
                  if (x.isValid === false) {
                    this.showErrorMessageStepCompleted = true;
                    this.stepCompletedErrorMessage = 'Sie haben diesen Schritt bereits abgeschlossen.';
                    this.coreService.disableGlobalLoading();
                    this.loading = false;
                    return;
                  }

                  this.sessionToken = x.sessionId;
                  this.session = x;

                  if(this.projectShortName) {
                    this.fileService.getFiles(this.projectShortName, this.tokenIdentifier, this.sessionToken).subscribe(
                      (y: any[]) => {
                        this.availableFiles = y;
                        this.prepareDownloadFiles();
                        this.prepareActConfiguration();
                        this.prepareRequiredUploadFiles();
                        this.generateForm();
                        this.filterFilesByCategory();

                        this.coreService.disableGlobalLoading();
                        this.loading = false;
                      }
                    );
                  }
                });
              }
            }
          }
        },
        complete: () => {
        },
        error: (err: Error) => {
          this.toastrService.error(err.message);
        }
      });
    }
  }

  filterFilesByCategory() {
    if (this.actRequiredFileCategories && this.availableFiles) {

      this.filesByCategory = {};
      this.actRequiredFileCategories.forEach(category => {
        // Filter files by both fileCategory and fileCategoryId
        const downloadedFileList = this.availableFiles.result.files;
        const files = Array.isArray(downloadedFileList)
          ? downloadedFileList.filter(file =>
              file.fileCategoryId === category.id || file.fileCategory === category.id
            )
          : [];

        this.filesByCategory[category.id] = files;

        const control = this.dynamicForm.get(category.id);
        if (control) {
          control.setValue(files.length);
          control.markAsTouched();
          control.updateValueAndValidity();
        }
      });

      // Update form validity
      this.dynamicForm.updateValueAndValidity();
    }
  }

  prepareDownloadFiles() {
    this.downloadService.getProjectDownloads().pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe({
        next: (res: any) => {
          const downloads = this.filterDownloadFiles(res);
          this.showDownloadbleFiles = downloads.length > 0;
        },
        error: () => {
          this.toastrService.error('Could not load Downloads');
          this.showDownloadbleFiles = false;
        },
        complete: () => {
        }
      }
    );
  }

  filterDownloadFiles(downloads: any) {
    if (!downloads || !Array.isArray(downloads)) {
      this.downloadFiles = [];
      return [];
    }

    const projectObject = downloads.filter((download: any) => {
      return download?.Project === this.projectShortName?.toUpperCase();
    });

    if (!projectObject || projectObject.length === 0 || !projectObject[0]?.Downloads) {
      this.downloadFiles = [];
      return [];
    }

    this.downloadFiles = projectObject[0].Downloads;
    return projectObject[0].Downloads;
  }

  prepareRequiredUploadFiles() {
    const sessionCategories = this.session?.project?.settings?.requiredFileCategories;
    const projectCategories = this.project.settings?.requiredFileCategories;
    const hasSessionCategories = Array.isArray(sessionCategories) && sessionCategories.length > 0;

    this.actRequiredFileCategories = hasSessionCategories
      ? sessionCategories
      : (projectCategories ?? []);
    this.uploadOfFilesRequired = (this.actRequiredFileCategories?.length ?? 0) > 0;
  }

  prepareActConfiguration() {
    if (!this.session?.project?.settings) {
      return;
    }

    this.projectSettings = this.session.project.settings;
    this.actConfiguration = this.session.project.settings.aktConfiguration;
    this.actFields = this.actConfiguration?.selectedFields;

    if (this.actFields) {
      this.actFields.sort((a, b) => a.order - b.order);
    }

    //todo: @Roland ibs_r (reference code for affiliates)
  }

  generateForm() {
    this.dynamicForm = new FormGroup({});

    if (this.actFields) {
      this.actFields.forEach((actField) => {
        const validators = [
          // Custom validator for required fields
          (control: AbstractControl) => {
            // If field is not required, it's always valid
            if (!actField.required) {
              return null;
            }

            // If required field has no value, it's invalid (regardless of touched state)
            if (!control.value || control.value === '') {
              return { required: true };
            }

            // Field is required and has a value - it's valid
            return null;
          }
        ];

        const control = new FormControl(
          '',  // initial value
          validators
        );

        // Mark as pristine and untouched
        control.markAsPristine();
        control.markAsUntouched();

        // For required fields, trigger validation to set initial invalid state
        if (actField.required) {
          control.updateValueAndValidity();
        }

        this.dynamicForm.addControl(
          actField.fieldName,
          control
        );
      });
    }

    if (this.actRequiredFileCategories) {
      this.actRequiredFileCategories.forEach(category => {
        const filesControl = new FormControl(null, [
          (control: AbstractControl) => {
            if (!control.touched) {
              return null;
            }
            const files = this.filesByCategory[category.id] || [];
            if (files.length < category.minimumFiles) {
              return { insufficientFiles: true };
            }
            return null;
          }
        ]);

        // Mark as pristine and untouched
        filesControl.markAsPristine();
        filesControl.markAsUntouched();

        this.dynamicForm.addControl(category.id, filesControl);

        // Subscribe to form value changes
        this.dynamicForm.get(category.id)?.valueChanges.subscribe(() => {
          this.dynamicForm.updateValueAndValidity();
        });
      });
    }

    // Subscribe to form status changes
    this.dynamicForm.statusChanges.subscribe(status => {

    });
  }

  public isFormValid(): boolean {
    if (!this.dynamicForm) {
      return false;
    }

    // Check if all required fields are filled
    const requiredFieldsValid = this.actFields?.every(field => {
      if (!field.required) {
        return true;
      }

      const control = this.dynamicForm.get(field.fieldName);

      return control?.value && control?.valid;
    }) ?? true;

    // Check if all file categories have required files
    const fileCategoriesValid = this.actRequiredFileCategories?.every(category => {
      const files = this.filesByCategory[category.id] || [];
      const fileCount = files.length;
      return fileCount >= category.minimumFiles;
    }) ?? true;

    return requiredFieldsValid && fileCategoriesValid;
  }

  checkActValid(): boolean {
    if (!this.dynamicForm) {
      return false;
    }

    // Check if required fields are valid
    let requiredFieldsValid = true;
    if (this.actFields) {
      requiredFieldsValid = this.actFields
        .filter(field => field.required)
        .every(field => {
          const control = this.dynamicForm.get(field.fieldName);
          return control && control.valid && control.value;
        });
    }

    // Check file upload requirement
    let fileUploadsValid = true;
    if (this.uploadOfFilesRequired && this.actRequiredFileCategories) {
      fileUploadsValid = this.actRequiredFileCategories.every(category => {
        const files = this.filesByCategory[category.id] || [];
        return files.length >= category.minimumFiles;
      });
    }

    return requiredFieldsValid && fileUploadsValid;
  }

  clickOnNextButton(): void {
    if (this.isFormValid()) {

      this.formDataService.setActFormData(this.dynamicForm.value);
      // Set the fromStep2 flag
      // step 4 = values-in-dispute component
      // step 3 = contract component

      sessionStorage.setItem('fromStep2', 'true');
      // Navigate to step3 (values-in-dispute component)

      this.coreService.enableGlobalLoading();
      this.router.navigate(['/registration/', this.projectShortName, 'step3', this.tokenIdentifier], {
        queryParams: { sessionId: this.sessionToken }
      });

    }
  }

  public refreshFiles() {
    if (this.projectShortName && this.tokenIdentifier && this.sessionToken) {
      this.fileService.getFiles(this.projectShortName, this.tokenIdentifier, this.sessionToken).subscribe(
        (files: any[]) => {
          this.availableFiles = files || [];
          this.filterFilesByCategory();
        },
        (error) => {
          this.toastrService.error('Fehler beim Abruf der Dateien');
        }
      );
    }
  }

  ngOnDestroy() {
  }

  getFormControl(fieldName: string): FormControl {
    if (!fieldName) {
      return new FormControl(null);
    }

    const control = this.dynamicForm.get(fieldName);

    if (!control) {
      // For file uploads, create the control if it doesn't exist
      if (this.actRequiredFileCategories?.some(cat => cat.id === fieldName)) {
        const filesControl = new FormControl(null, [
          (control: AbstractControl) => {
            if (!control.touched) {
              return null;
            }
            const files = this.filesByCategory[fieldName] || [];
            if (files.length < (this.actRequiredFileCategories?.find(cat => cat.id === fieldName)?.minimumFiles || 0)) {
              return { insufficientFiles: true };
            }
            return null;
          }
        ]);
        this.dynamicForm.addControl(fieldName, filesControl);
        return filesControl;
      }

      // For other fields, create a basic control
      const newControl = new FormControl(null);
      this.dynamicForm.addControl(fieldName, newControl);
      return newControl;
    }

    return control as FormControl;
  }
}
